/**
 * Maps backend auth error shapes to user-facing messages.
 * Backend may return: error, nonce, signature, address, message, email (arrays or strings).
 */

/**
 * Get a user-facing message for Magic auth errors
 * @param {object} err - Caught error (may have err.error from request service or err.errors)
 * @returns {string}
 */
export function getMagicAuthErrorMessage(err) {
  const body = err?.error ?? err?.errors ?? {}
  const detail = Array.isArray(body.errors) && body.errors[0]?.detail ? body.errors[0].detail : null
  if (detail) return detail
  if (typeof body.error === 'string') return body.error
  return err?.message || 'Magic login failed. Please try again.'
}

/**
 * Get a user-facing message for SIWE (wallet) auth errors (challenge + verify)
 * @param {object} err - Caught error (may have err.data or err.error with nonce, signature, address, message, email, error)
 * @returns {string}
 */
export function getWalletAuthErrorMessage(err) {
  const data = err?.data ?? err?.error ?? {}
  const first = (arr) => (Array.isArray(arr) ? arr[0] : arr)
  if (first(data.nonce)) return first(data.nonce) || 'This sign-in request has expired or was already used. Please try again.'
  if (first(data.signature)) return first(data.signature) || 'Invalid signature. Please sign the message again.'
  if (first(data.message)) return first(data.message) || 'Invalid sign-in message. Please try again.'
  if (data.error) return typeof data.error === 'string' ? data.error : first(data.error)
  if (first(data.address)) return first(data.address) || 'Invalid wallet address.'
  if (first(data.email)) return first(data.email) || 'Please enter a valid email address.'
  return err?.message || 'Wallet sign-in failed. Please try again.'
}

/**
 * Get a user-facing message for Safe registration errors
 * @param {object} err - Caught error (may have err.data or err.error)
 * @returns {string}
 */
export function getSafeRegistrationErrorMessage(err) {
  const data = err?.data ?? err?.error ?? {}
  const first = (arr) => (Array.isArray(arr) ? arr[0] : arr)
  if (data.error) return typeof data.error === 'string' ? data.error : first(data.error)
  if (first(data.safe_address)) return first(data.safe_address)
  if (first(data.chain_id)) return first(data.chain_id)
  return err?.message || 'Failed to register Safe. You can try again from settings.'
}
